// ConfirmDialog — modal dialog pro destruktivni / dulezite akce.
// Pouziti:
//   const [open, setOpen] = useState(false);
//   const [pending, setPending] = useState(null); // co se ma vykonat
//
//   <ConfirmDialog
//     open={open}
//     title="Spustit automatizaci?"
//     description={`Opravdu chcete spustit "${pending?.name}"?`}
//     confirmLabel="Spustit"
//     onConfirm={() => { handleRun(pending); setOpen(false); }}
//     onCancel={() => setOpen(false)}
//     variant="primary"  // primary | danger | warning | save
//     actionKey="run:sync-bookings-cache"  // optional. Pokud je zadan, zobrazi
//                                          // checkbox "Neptej se v dalsich 60 minut".
//                                          // Po potvrzeni s checkbox se ulozi
//                                          // do localStorage timestamp + 60 min.
//     bypassMinutes={60}                   // optional, default 60
//   />
//
// Helpery (pro callera, aby si overeil ze ma bypass aktivni):
//   import { isConfirmBypassed, clearConfirmBypass } from "./ConfirmDialog.jsx";
//   if (isConfirmBypassed("run:sync-bookings-cache")) runDirectly();
//   else openDialog();

import React, { useEffect, useState } from "react";
import useEscClose from "../lib/useEscClose.js";
import { createPortal } from "react-dom";
import { AlertTriangle, Play, Save, X } from "lucide-react";

const VARIANTS = {
  primary: {
    iconBg: "bg-brand-light text-brand-deep",
    icon: Play,
    button: "bg-brand hover:bg-brand-deep text-white",
  },
  danger: {
    iconBg: "bg-rose-100 text-rose-600",
    icon: AlertTriangle,
    button: "bg-rose-600 hover:bg-rose-700 text-white",
  },
  warning: {
    iconBg: "bg-amber-100 text-amber-600",
    icon: AlertTriangle,
    button: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  save: {
    iconBg: "bg-emerald-100 text-emerald-600",
    icon: Save,
    button: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
};

// localStorage prefix pro bypass keys. Per-actionKey, ne globalni.
const BYPASS_PREFIX = "confirmBypassUntil:";
// Globální bypass — sdílený klíč pro celou kategorii akcí (např. všechny crony).
// Caller předá `actionKey="global:run"` a checkbox v dialogu odrazí "platí pro vše".
const GLOBAL_PREFIX = "global:";

// Bezpecne cteni z localStorage (private mode browseru muze hodit vyjimku).
function safeRead(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

// Pokud je actionKey aktivni v ramci 60 min okna, vrati true. Jinak false.
// Pokud je timestamp expirovany, smaze ho (lazy cleanup).
// Bonus: pokud existuje globální bypass se stejnou kategorií (`global:<kategorie>`),
// počítá se taky. Caller pasuje například actionKey="run:bonusove" — funkce zkusí
// nejdřív přesný klíč, pak hledá `global:run` (stripped na první dvojtečku).
export function isConfirmBypassed(actionKey) {
  if (!actionKey) return false;
  // 1) Per-action bypass
  const raw = safeRead(BYPASS_PREFIX + actionKey);
  if (raw) {
    const until = Number(raw);
    if (Number.isFinite(until) && until > Date.now()) return true;
    safeRemove(BYPASS_PREFIX + actionKey);
  }
  // 2) Global bypass — kategorie před první dvojtečkou (např. "run:foo" → "run")
  const colonIdx = actionKey.indexOf(":");
  if (colonIdx > 0) {
    const category = actionKey.substring(0, colonIdx);
    const globalRaw = safeRead(BYPASS_PREFIX + GLOBAL_PREFIX + category);
    if (globalRaw) {
      const gUntil = Number(globalRaw);
      if (Number.isFinite(gUntil) && gUntil > Date.now()) return true;
      safeRemove(BYPASS_PREFIX + GLOBAL_PREFIX + category);
    }
  }
  return false;
}

// Vrati timestamp v ms, do kdy je bypass aktivni. 0 = neni.
// Vraci max z (per-action, global-by-category).
export function getConfirmBypassUntil(actionKey) {
  if (!actionKey) return 0;
  let max = 0;
  const raw = safeRead(BYPASS_PREFIX + actionKey);
  if (raw) {
    const until = Number(raw);
    if (Number.isFinite(until) && until > Date.now()) max = until;
  }
  const colonIdx = actionKey.indexOf(":");
  if (colonIdx > 0) {
    const category = actionKey.substring(0, colonIdx);
    const gRaw = safeRead(BYPASS_PREFIX + GLOBAL_PREFIX + category);
    if (gRaw) {
      const gUntil = Number(gRaw);
      if (Number.isFinite(gUntil) && gUntil > Date.now() && gUntil > max) max = gUntil;
    }
  }
  return max;
}

export function setConfirmBypass(actionKey, minutes = 60) {
  if (!actionKey) return;
  const until = Date.now() + Math.max(1, minutes) * 60 * 1000;
  safeWrite(BYPASS_PREFIX + actionKey, String(until));
}

// Globální bypass pro celou kategorii (např. "run" platí pro "run:bonusove" + všechny další).
export function setGlobalConfirmBypass(category, minutes = 60) {
  if (!category) return;
  const until = Date.now() + Math.max(1, minutes) * 60 * 1000;
  safeWrite(BYPASS_PREFIX + GLOBAL_PREFIX + category, String(until));
}

export function getGlobalConfirmBypassUntil(category) {
  if (!category) return 0;
  const raw = safeRead(BYPASS_PREFIX + GLOBAL_PREFIX + category);
  if (!raw) return 0;
  const until = Number(raw);
  if (!Number.isFinite(until) || until <= Date.now()) return 0;
  return until;
}

export function clearGlobalConfirmBypass(category) {
  if (!category) return;
  safeRemove(BYPASS_PREFIX + GLOBAL_PREFIX + category);
}

export function clearConfirmBypass(actionKey) {
  if (!actionKey) return;
  safeRemove(BYPASS_PREFIX + actionKey);
}

export default function ConfirmDialog({
  open,
  title,
  description,
  details,
  confirmLabel = "Potvrdit",
  cancelLabel = "Zrušit",
  variant = "primary",
  onConfirm,
  onCancel,
  loading = false,
  actionKey,
  bypassMinutes = 60,
  // bypassScope: "per-action" (default) | "global"
  // global: checkbox uloží bypass pro celou kategorii (před první ":") — platí
  // pro VŠECHNY akce stejné kategorie (např. všechny crony).
  bypassScope = "per-action",
}) {
  useEscClose(onCancel, !!open); // Esc = Zrusit (jen kdyz je dialog otevreny)
  // Lokalni stav checkboxu "Neptej se v dalsich 60 min". Reset pri otevreni.
  const [skipNext, setSkipNext] = useState(false);
  useEffect(() => {
    if (open) setSkipNext(false);
  }, [open]);

  // Caller dostane info, ze user zaskrtnul checkbox. Bypass timestamp ulozime
  // tady (single source of truth), ne v calleru.
  const handleConfirm = () => {
    if (skipNext && actionKey) {
      if (bypassScope === "global") {
        // Extrahuj kategorii (před první dvojtečkou)
        const colonIdx = actionKey.indexOf(":");
        const category = colonIdx > 0 ? actionKey.substring(0, colonIdx) : actionKey;
        setGlobalConfirmBypass(category, bypassMinutes);
      } else {
        setConfirmBypass(actionKey, bypassMinutes);
      }
    }
    onConfirm?.();
  };

  // ESC closes, Enter confirms (s respektovanim checkboxu).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel?.();
      if (e.key === "Enter" && !loading) handleConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, onConfirm, onCancel, skipNext, actionKey]);

  if (!open) return null;

  const v = VARIANTS[variant] || VARIANTS.primary;
  const Icon = v.icon;

  return createPortal(
    <div
      className="nad-klavesnici fixed inset-0 z-[60] flex animate-fade-in items-end justify-center overflow-hidden bg-brand-dark/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel?.();
      }}
    >
      <div
        className="flex w-full max-w-md animate-sheet-up flex-col rounded-t-2xl bg-white p-4 shadow-2xl sm:animate-zoom-in sm:rounded-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`p-3 rounded-xl shrink-0 ${v.iconBg}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 leading-tight">
              {title}
            </h3>
            {description && (
              // div místo p — caller může předat JSX (multi-line, badges, ikony)
              <div className="text-sm text-slate-600 mt-1 leading-relaxed">
                {description}
              </div>
            )}
          </div>
          <button
            onClick={() => !loading && onCancel?.()}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            aria-label="Zavřít"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Details (optional) */}
        {details && (
          <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm text-slate-700 font-mono break-words">
            {details}
          </div>
        )}

        {/* Opt-out checkbox (jen kdyz je actionKey zadan) */}
        {actionKey && (
          <label className="flex items-start gap-2 mt-4 px-1 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={skipNext}
              onChange={(e) => setSkipNext(e.target.checked)}
              disabled={loading}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              {bypassScope === "global" ? (
                <>
                  Neptat se na <strong>žádnou automatizaci</strong> v následujících{" "}
                  <strong>{bypassMinutes} minutách</strong>.
                </>
              ) : (
                <>
                  Neptat se znovu na tuto akci v následujících{" "}
                  <strong>{bypassMinutes} minutách</strong>.
                </>
              )}
              <span className="block text-xs text-slate-500 mt-0.5">
                Platí pouze v tomto prohlížeči. Lze zrušit z hlavičky stránky.
              </span>
            </span>
          </label>
        )}

        {/* Actions */}
        <div className="patka-okna patka-dole mt-6" data-patka-okna>
          {cancelLabel !== null && (
            <button onClick={onCancel} disabled={loading} className="btn btn-secondary">
              {cancelLabel}
            </button>
          )}
          <button onClick={handleConfirm} disabled={loading} className={`btn ${v.button}`}>
            {loading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
